/**
 * LeadForm Controller
 * Handles multi-step form submission with validation and email notifications
 */

interface FormData {
  page: string;
  offerta_for: string;
  project_type: string;
  service_type: string;
  name: string;
  email: string;
  phone: string;
  address: string;
}

export class LeadFormController {
  private readonly formId = '#quote-form';
  private readonly form: HTMLFormElement;
  private readonly formData: Partial<FormData> = {};

  constructor() {
    const form = document.querySelector(this.formId);
    if (!(form instanceof HTMLFormElement)) {
      throw new TypeError(`Form ${this.formId} not found`);
    }
    this.form = form;
    this.init();
  }

  private init(): void {
    // Initialize event listeners for step navigation
    this.attachStepNavigation();
    this.attachFormSubmit();
    this.clearErrorsOnInput();
  }

  private attachStepNavigation(): void {
    // Step 1 to Step 2
    const next1Btn = this.form.querySelector('#next-step-1-btn');
    next1Btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.validateAndNext(1, 2);
    });

    // Step 2: Next
    const next2Btn = this.form.querySelector('#next-step-2-btn');
    next2Btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.validateAndNext(2, 3);
    });

    // Step 2: Previous
    const prev2Btn = this.form.querySelector('#prev-step-2-btn');
    prev2Btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToStep(1);
      this.clearStepErrors(2);
    });

    // Step 3: Previous
    const prev3Btn = this.form.querySelector('#prev-step-3-btn');
    prev3Btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToStep(2);
      this.clearStepErrors(3);
    });
  }

  private attachFormSubmit(): void {
    this.form.addEventListener('submit', (e) => {
      void this.handleSubmit(e);
    });
  }

  private clearErrorsOnInput(): void {
    // Clear errors when user starts typing/changing fields
    const inputs = this.form.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]');
    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        const group = input.closest('.form-group');
        const errorEl = group?.querySelector('.form-error');
        if (errorEl) {
          errorEl.classList.add('hidden');
          input.classList.remove('border-red-600', 'bg-red-50');
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
        }
      });
    });

    // Clear errors when user selects radio buttons
    const radios = this.form.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const formStep = radio.closest('.form-step');
        const errors = formStep?.querySelectorAll('.form-error');
        errors?.forEach((error) => { error.classList.add('hidden'); });
      });
    });
  }

  private clearStepErrors(step: number): void {
    const stepEl = this.form.querySelector(`#form-step-${String(step)}`);
    const errors = stepEl?.querySelectorAll('.form-error');
    errors?.forEach((error) => { error.classList.add('hidden'); });
  }

  private validateAndNext(fromStep: number, toStep: number): void {
    if (this.validateStep(fromStep)) {
      this.goToStep(toStep);
      this.clearStepErrors(fromStep);
    }
  }

  private validateStep(step: number): boolean {
    switch (step) {
      case 1:
        return this.validateProjectType();
      case 2:
        return this.validateServiceType();
      case 3:
        return this.validateContactDetails();
      default:
        return true;
    }
  }

  private validateProjectType(): boolean {
    const projectType = this.getCheckedValue('project_type');
    if (!projectType) {
      this.showStepError(1, 'Selecteer alstublieft een woningtype');
      return false;
    }
    this.formData.project_type = projectType;
    return true;
  }

  private validateServiceType(): boolean {
    const serviceType = this.getCheckedValue('service_type');
    if (!serviceType) {
      this.showStepError(2, 'Selecteer alstublieft een dienst');
      return false;
    }
    this.formData.service_type = serviceType;
    return true;
  }

  private validateContactDetails(): boolean {
    const name = this.getInputValue('#name');
    const email = this.getInputValue('#email');
    const phone = this.getInputValue('#phone');

    let hasError = false;

    if (!name || name.length < 2) {
      this.showError('#name', 'Vul uw naam in (minimaal 2 tekens)');
      hasError = true;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError('#email', 'Vul een geldig e-mailadres in');
      hasError = true;
    }
    if (!phone || phone.length < 10) {
      this.showError('#phone', 'Vul een geldig telefoonnummer in');
      hasError = true;
    }

    if (hasError) {
      return false;
    }

    this.formData.name = name;
    this.formData.email = email;
    this.formData.phone = phone;
    this.formData.address = this.getInputValue('#address');

    return true;
  }

  private getInputValue(selector: string): string {
    const input = this.form.querySelector<HTMLInputElement>(selector);
    return input ? input.value.trim() : '';
  }

  private getCheckedValue(name: string): string | null {
    const input = this.form.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
    return input?.value ?? null;
  }

  private goToStep(step: number): void {
    // Hide all steps
    const steps = this.form.querySelectorAll('.form-step');
    steps.forEach((el) => { el.classList.add('hidden'); });

    // Show target step
    const targetStep = this.form.querySelector(`#form-step-${String(step)}`);
    if (targetStep) {
      targetStep.classList.remove('hidden');
    }

    // Update progress indicator
    this.updateProgress(step);
  }

  private updateProgress(step: number): void {
    // Update step counter
    const stepCounter = this.form.querySelector('#current-step');
    if (stepCounter) {
      stepCounter.textContent = String(step);
    }

    // Update step label
    const stepLabel = this.form.querySelector('#step-label');
    if (stepLabel) {
      const labels = {
        1: 'Type woning',
        2: 'Dienst',
        3: 'Contactgegevens'
      } as const;
      stepLabel.textContent = labels[step as keyof typeof labels];
    }

    // Update progress bar width
    const progressBar = this.form.querySelector<HTMLElement>('#progress-bar');
    if (progressBar) {
      const percentages = {
        1: '33%',
        2: '66%',
        3: '100%'
      } as const;
      progressBar.style.width = percentages[step as keyof typeof percentages];
    }
  }

  private showStepError(step: number, message: string): void {
    const stepEl = this.form.querySelector(`#form-step-${String(step)}`);
    if (!stepEl) return;

    // Remove existing error message if any
    const existingError = stepEl.querySelector('.step-error-message');
    if (existingError) {
      existingError.remove();
    }

    // Add error message at the top
    const errorDiv = document.createElement('div');
    errorDiv.className = 'step-error-message bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2';
    errorDiv.innerHTML = `
      <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
      </svg>
      <span>${message}</span>
    `;
    stepEl.insertBefore(errorDiv, stepEl.firstChild);

    // Remove error after 5 seconds or on user interaction
    const removeError = () => {
      errorDiv.classList.add('opacity-0', 'transition-opacity', 'duration-300');
      setTimeout(() => { errorDiv.remove(); }, 300);
    };

    setTimeout(removeError, 5000);

    const inputs = stepEl.querySelectorAll('input, select');
    inputs.forEach((input) => {
      input.addEventListener('change', removeError, { once: true });
    });
  }

  private showError(fieldId: string, message: string): void {
    const field = this.form.querySelector<HTMLInputElement>(fieldId);
    if (!field) return;

    const group = field.closest('.form-group');
    const errorId = `${field.id || field.name}-error`;
    let errorEl = group?.querySelector<HTMLElement>('.form-error');

    if (errorEl) {
      errorEl.id = errorId;
      errorEl.innerHTML = `
        <svg class="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18.101 12.93a1 1 0 00-1.414-1.414L10 15.586 3.313 8.899a1 1 0 00-1.414 1.414l8 8a1 1 0 001.414 0l8-8z" clip-rule="evenodd"></path>
        </svg>
        <span>${message}</span>
      `;
    } else {
      errorEl = document.createElement('span');
      errorEl.id = errorId;
      errorEl.className = 'form-error text-red-600 text-sm mt-2 flex items-center gap-1';
      errorEl.setAttribute('aria-live', 'assertive');
      errorEl.innerHTML = `
        <svg class="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18.101 12.93a1 1 0 00-1.414-1.414L10 15.586 3.313 8.899a1 1 0 00-1.414 1.414l8 8a1 1 0 001.414 0l8-8z" clip-rule="evenodd"></path>
        </svg>
        <span>${message}</span>
      `;
      group?.appendChild(errorEl);
    }

    errorEl.classList.remove('hidden');
    field.classList.add('border-red-600', 'bg-red-50');
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', errorId);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (!this.validateStep(3)) {
      return;
    }

    const submitBtn = this.getSubmitButton();
    if (!submitBtn) return;

    const originalText = this.setSubmittingState(submitBtn);

    try {
      const response = await this.submitForm();

      this.showSuccessMessage();
      const redirectUrl = await this.resolveRedirectUrl(response);
      this.redirectAfterDelay(redirectUrl);
    } catch (error) {
      console.error('Form submission error:', error);
      this.showSubmitError();
      this.resetSubmitButton(submitBtn, originalText);
    }
  }

  private getSubmitButton(): HTMLButtonElement | null {
    return this.form.querySelector('button[type="submit"]');
  }

  private setSubmittingState(submitBtn: HTMLButtonElement): string | null {
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.classList.add('opacity-75', 'cursor-not-allowed');
    submitBtn.innerHTML = `
      <span class="inline-flex items-center gap-2">
        <svg class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Verzenden...</span>
      </span>
    `;
    return originalText;
  }

  private resetSubmitButton(submitBtn: HTMLButtonElement, originalText: string | null): void {
    submitBtn.disabled = false;
    submitBtn.classList.remove('opacity-75', 'cursor-not-allowed');
    submitBtn.innerHTML = originalText ?? 'Ontvang mijn prijsindicatie';
  }

  private async submitForm(): Promise<Response> {
    const formDataToSend = new FormData();

    Object.entries(this.formData).forEach(([key, value]) => {
      if (typeof value === 'string') {
        formDataToSend.append(key, value);
      }
    });

    const page = this.getInputValue('input[name="page"]');
    if (page) formDataToSend.append('page', page);

    // Bot protection fields
    const formTime = this.form.querySelector<HTMLInputElement>('input[name="_form_time"]');
    if (formTime?.value) formDataToSend.append('_form_time', formTime.value);
    formDataToSend.append('website', ''); // honeypot — must be empty

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const response = await fetch('/lead-form/submit', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      body: formDataToSend
    });

    if (!response.ok) {
      throw new Error('Submission failed');
    }

    return response;
  }

  private showSuccessMessage(): void {
    const successDiv = document.createElement('div');
    successDiv.className = 'bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded mb-6 flex items-center gap-2';
    successDiv.innerHTML = `
      <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
      </svg>
      <span>Aanvraag verzonden! U wordt doorgestuurd...</span>
    `;
    this.form.insertBefore(successDiv, this.form.firstChild);
  }

  private showSubmitError(): void {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2';
    errorDiv.innerHTML = `
      <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
      </svg>
      <span>Er ging iets mis. Controleer uw internetverbinding en probeer opnieuw.</span>
    `;
    this.form.insertBefore(errorDiv, this.form.firstChild);
  }

  private async resolveRedirectUrl(response: Response): Promise<string> {
    const contentType = response.headers.get('content-type') ?? '';
    let redirectUrl = '/bedankt';

    if (contentType.includes('application/json')) {
      const data = (await response.json()) as { redirect?: string };
      redirectUrl = data.redirect ?? redirectUrl;
    } else if (response.redirected) {
      const url = new URL(response.url);
      redirectUrl = `${url.pathname}${url.search}${url.hash}`;
    }

    return redirectUrl;
  }

  private redirectAfterDelay(redirectUrl: string, delayMs = 1500): void {
    setTimeout(() => {
      globalThis.location.href = redirectUrl;
    }, delayMs);
  }
}

// Auto-initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('#quote-form')) {
    (globalThis as typeof globalThis & { leadFormController?: LeadFormController }).leadFormController = new LeadFormController();
  }
});
